import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

if (!process.env.REACT_APP_BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required');
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const resetErrorMessage = (detail) => {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) return detail.map(item => typeof item?.msg === 'string' ? item.msg : '').filter(Boolean).join(' ') || 'Could not update your password. Please try again.';
  return 'Could not update your password. Please try again.';
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const submitting = useRef(false);

  useEffect(() => {
    setError(token ? null : "Invalid reset link. Please request a new password reset.");
    setSubmitError(''); setSuccess(false); setPassword(''); setConfirmPassword('');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || !token) return;
    
    if (password.length < 6) {
      setSubmitError("Password must be at least 6 characters");
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      setSubmitError("Passwords do not match");
      toast.error("Passwords do not match");
      return;
    }
    
    submitting.current = true;
    setSubmitError(''); setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        password
      });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      const msg = resetErrorMessage(err.response?.data?.detail);
      toast.error(msg);
      if (err.response?.status === 400 && /expired|invalid.*(link|token)/i.test(msg)) {
        setError(msg);
      } else {
        setSubmitError(msg);
      }
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)] text-[var(--ps-text-primary)] flex flex-col items-center justify-center px-6 py-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-32 h-32 mx-auto mb-4">
            <img 
              src="/images/par-seeker-logo.png" 
              alt="Par Seeker" 
              className="w-full h-full object-contain"
              style={{
                maskImage: 'radial-gradient(ellipse 85% 80% at center, black 50%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 85% 80% at center, black 50%, transparent 100%)'
              }}
            />
          </div>
          <h1 className="text-2xl font-black tracking-tight uppercase">
            <span className="text-green-700">Par</span> <span className="text-amber-500">Seeker</span>
          </h1>
        </div>

        <Card className="animate-fade-in">
          <CardHeader className="text-center">
            {error ? (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <CardTitle className="text-xl text-red-600" data-testid="reset-link-error-title">Reset Link Unavailable</CardTitle>
              </>
            ) : success ? (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-700" />
                </div>
                <CardTitle className="text-xl text-green-700" data-testid="reset-password-success-title">Password Reset!</CardTitle>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-green-700" />
                </div>
                <CardTitle className="text-xl" data-testid="reset-password-title">Create New Password</CardTitle>
              </>
            )}
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="text-center space-y-4">
                <p className="text-[var(--ps-text-secondary)]" role="alert" data-testid="reset-link-error">{error}</p>
                <Button
                  className="w-full bg-green-700 hover:bg-green-800"
                  onClick={() => navigate('/')}
                  data-testid="reset-error-back-to-login"
                >
                  Back to Sign In
                </Button>
              </div>
            ) : success ? (
              <div className="text-center space-y-4">
                <p className="text-[var(--ps-text-secondary)]" role="status" data-testid="reset-password-success">
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <Button
                  className="w-full bg-green-700 hover:bg-green-800"
                  onClick={() => navigate('/')}
                  data-testid="back-to-login-btn"
                >
                  Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-4" data-testid="reset-password-form">
                <p className="text-stone-600 text-sm text-center mb-4">
                  Enter your new password below.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12"
                    data-testid="new-password-input"
                    autoComplete="new-password"
                    disabled={loading}
                    minLength={6}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12"
                    data-testid="confirm-password-input"
                    autoComplete="new-password"
                    disabled={loading}
                    minLength={6}
                    required
                  />
                </div>
                {submitError && <p role="alert" className="text-sm text-red-600" data-testid="reset-password-submit-error">{submitError}</p>}
                <Button 
                  type="submit" 
                  className="w-full bg-green-700 hover:bg-green-800"
                  disabled={loading || !token}
                  data-testid="reset-password-submit"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5 border-white border-t-transparent" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/')}
                  data-testid="reset-form-back-to-login"
                >
                  Back to Sign In
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
